import type { QuestionView } from "./assistant-types";

const SETTLED_STATUSES: ReadonlySet<QuestionView["status"]> = new Set([
  "answered",
  "failed",
  "cancelled",
]);
const MS_PER_SECOND = 1_000;

export class AnswerTimeoutError extends Error {
  constructor(deadlineMs: number) {
    super(
      `The Support Bot is still working on this question after ${deadlineMs / MS_PER_SECOND} s. Try again shortly.`,
    );
    this.name = "AnswerTimeoutError";
  }
}

export interface AnswerPolling {
  fetchQuestion: (questionId: string) => Promise<QuestionView>;
  pause: (ms: number) => Promise<void>;
  now: () => number;
}

/**
 * Fetches the Question every `intervalMs` until it is answered, failed or cancelled. Each fetch is a short request,
 * as the API Gateway in front of support-bot cuts any request off at 29 s and an Answer can take longer.
 */
export async function waitForAnswer(
  asked: QuestionView,
  polling: AnswerPolling,
  { intervalMs, deadlineMs }: { intervalMs: number; deadlineMs: number },
): Promise<QuestionView> {
  const giveUpAt = polling.now() + deadlineMs;
  let latest = asked;
  while (!SETTLED_STATUSES.has(latest.status)) {
    if (polling.now() >= giveUpAt) throw new AnswerTimeoutError(deadlineMs);
    await polling.pause(intervalMs);
    latest = await polling.fetchQuestion(latest.questionId);
  }
  return latest;
}
